import { useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import cities from "../data/usCitiesStates.json";

// Umbral mínimo de caracteres antes de buscar (evita listar miles de opciones con 1 letra).
export const MIN_QUERY_LENGTH = 3;
// Máximo de resultados mostrados en el desplegable.
export const MAX_RESULTS = 8;

function normalize(str) {
  return (str || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // quita tildes/diacríticos básicos
    .toLowerCase()
    .trim();
}

/**
 * Filtra el catálogo local de ciudades por coincidencia de prefijo o
 * substring (case-insensitive, sin tildes). Prefijo primero, luego
 * substring. Puro, sin efectos — testeable sin DOM.
 *
 * @param {string} query
 * @param {Array<{city:string,state:string}>} catalog
 * @param {number} limit
 */
export function filterCities(query, catalog = cities, limit = MAX_RESULTS) {
  const q = normalize(query);
  if (q.length < MIN_QUERY_LENGTH) return [];

  const prefixMatches = [];
  const substringMatches = [];

  for (const entry of catalog) {
    const cityNorm = normalize(entry.city);
    if (cityNorm.startsWith(q)) {
      prefixMatches.push(entry);
    } else if (cityNorm.includes(q)) {
      substringMatches.push(entry);
    }
    if (prefixMatches.length >= limit) break;
  }

  const combined = [...prefixMatches, ...substringMatches];
  return combined.slice(0, limit);
}

/**
 * Autocompletado local (sin fetch) de ciudad/estado para campos de texto
 * libre. No bloquea el guardado: el usuario puede seguir escribiendo
 * cualquier texto que no esté en el catálogo.
 *
 * Props:
 *  - cityValue: valor actual del input de ciudad
 *  - onCityChange: (text) => void — se llama en cada tecla (texto libre)
 *  - onSelect: ({city, state}) => void — se llama al elegir una opción
 *  - cityTestId / stateTestId: data-testid a preservar en el input de ciudad
 *  - placeholder, maxLength, className
 */
export default function CityStateAutocomplete({
  cityValue = "",
  onCityChange,
  onSelect,
  cityTestId = "city-autocomplete-input",
  placeholder = "",
  maxLength = 100,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const blurTimeout = useRef(null);

  const options = useMemo(() => filterCities(cityValue), [cityValue]);

  const handleChange = (e) => {
    onCityChange && onCityChange(e.target.value);
    setOpen(true);
  };

  const handlePick = (opt) => {
    onSelect && onSelect({ city: opt.city, state: opt.state });
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        value={cityValue}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Pequeño delay para permitir el onMouseDown del item antes de cerrar.
          blurTimeout.current = setTimeout(() => setOpen(false), 150);
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`bg-secondary border-border ${className}`}
        data-testid={cityTestId}
        autoComplete="off"
      />
      {open && options.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-border bg-card shadow-md text-sm"
          data-testid={`${cityTestId}-options`}
        >
          {options.map((opt, idx) => (
            <li
              key={`${opt.city}-${opt.state}-${idx}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (blurTimeout.current) clearTimeout(blurTimeout.current);
                handlePick(opt);
              }}
              className="px-3 py-2 cursor-pointer hover:bg-secondary"
              data-testid={`${cityTestId}-option-${idx}`}
            >
              {opt.city}, {opt.state}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
